import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import IdeaCard from './IdeaCard';
import IdeaCardExpanded from './IdeaCardExpanded';
import { useSupabase } from '../../../contexts/SupabaseContext';
import type { Idea } from '../../../types/database';
import { cn } from '../../../lib/utils';

// Summary bar buttons
const SummaryButton: React.FC<{
  label: string;
  count?: number;
  active?: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'text-sm font-medium px-2 py-1 rounded-md transition-colors',
      active ? 'bg-primary text-white' : 'hover:bg-muted'
    )}
  >
    {label} {count !== undefined && <span className="opacity-70">({count})</span>}
  </button>
);

const Ideas: React.FC = () => {
  const {
    ideas,
    loadIdeas,
    createIdea,
    updateIdea,
    deleteIdea,
    promoteIdeaToProject,
  } = useSupabase();

  // local create-in-progress flag to avoid duplicate clicks
  const [creating, setCreating] = useState(false);

  // filter state
  type Filter = 'all' | 'top' | 'archived';
  const [filter, setFilter] = useState<Filter>('all');

  const [activeIdea, setActiveIdea] = useState<Idea | null>(null);

  useEffect(() => {
    loadIdeas();
  }, [loadIdeas]);

  // derived counts & filtered list
  const totalIdeas = ideas.filter(i => !i.archived).length;
  const topIdeas = ideas.filter(i => !i.archived && (i.rank ?? 5) >= 7).length;
  const archivedIdeas = ideas.filter(i => i.archived).length;

  const filteredIdeas = useMemo(() => {
    switch (filter) {
      case 'top':
        return ideas.filter(i => !i.archived && (i.rank ?? 5) >= 7);
      case 'archived':
        return ideas.filter(i => i.archived);
      default:
        return ideas.filter(i => !i.archived);
    }
  }, [ideas, filter]);

  const handleNewIdeaClick = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const newIdea = await createIdea({
        title: '',
        description: null,
        rank: 5,
        archived: false,
      } as any);
      setActiveIdea(newIdea);
    } finally {
      setCreating(false);
    }
  };

  const promptRank = async (idea: Idea) => {
    const val = window.prompt('Set rank 1-10', String(idea.rank ?? 5));
    if (!val) return;
    const num = Number(val);
    if (isNaN(num) || num < 1 || num > 10) return alert('Invalid rank');
    await updateIdea(idea.id, { rank: num });
  };

  const toggleArchive = async (idea: Idea) => {
    await updateIdea(idea.id, { archived: !idea.archived });
  };

  return (
    <div className="p-4 space-y-4">
      {/* Summary bar */}
      <div className="grid grid-cols-4 gap-2">
        <SummaryButton
          label="Total Ideas"
          count={totalIdeas}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <SummaryButton
          label="Top Ideas"
          count={topIdeas}
          active={filter === 'top'}
          onClick={() => setFilter('top')}
        />
        <SummaryButton
          label="Archived Ideas"
          count={archivedIdeas}
          active={filter === 'archived'}
          onClick={() => setFilter('archived')}
        />
        <SummaryButton
          label="Idea Maker 🤖"
          onClick={() => alert('Idea Maker is under construction 🚧')}
        />
      </div>

      {/* create new idea trigger */}
      <button
        onClick={handleNewIdeaClick}
        className="inline-flex items-center gap-1 text-sm px-3 py-1 border rounded-md hover:bg-muted"
      >
        <Plus size={16} /> New Idea
      </button>

      {/* ideas grid */}
      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
        {filteredIdeas.map((idea) => {
          const isExpanded = activeIdea?.id === idea.id;
          const commonProps = {
            promote: () => promoteIdeaToProject(idea.id),
            promptRank: () => promptRank(idea),
            toggleArchive: () => toggleArchive(idea),
            deleteIdea: () => deleteIdea(idea.id),
          };

          return isExpanded ? (
            <IdeaCardExpanded
              key={idea.id}
              idea={idea}
              onCollapse={() => setActiveIdea(null)}
              promote={commonProps.promote}
              deleteIdea={commonProps.deleteIdea}
            />
          ) : (
            <IdeaCard
              key={idea.id}
              idea={idea}
              onExpand={() => setActiveIdea(idea)}
              {...commonProps}
            />
          );
        })}
        {/* placeholder when none */}
        {filteredIdeas.length === 0 && (
          <div className="text-muted-foreground text-sm col-span-full text-center py-8">
            No ideas here yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default Ideas; 